import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { RecordValue, type RecordValueSpec } from "./record-value";

export type ValueChangeItem = {
  label: string;
  from?: RecordValueSpec;
  to: RecordValueSpec;
};

/** One field, as it was and as it is. */
const Change = ({ change }: { change: ValueChangeItem }) => (
  <Stack direction="row" align="center" gap={2} wrap className="min-w-0">
    <Text variant="body" tone="muted" render={<span />} className="shrink-0">
      {change.label}
    </Text>
    {change.from && <RecordValue spec={change.from} />}
    {change.from && (
      <ArrowRightIcon
        aria-hidden
        className="pointer-events-none size-3 shrink-0 text-subtlest"
      />
    )}
    <RecordValue spec={change.to} />
  </Stack>
);

const SHOWN = 3;

/**
 * The rows under an activity line: each field's old value, then its new one.
 * More than a few fold behind "Show all changes".
 */
export const ValueChanges = ({
  changes,
  className,
}: {
  changes: ValueChangeItem[];
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  if (changes.length === 0) return null;
  const shown = open ? changes : changes.slice(0, SHOWN);
  return (
    <Stack gap={1} className={className}>
      {shown.map((change) => (
        <Change key={change.label} change={change} />
      ))}
      {changes.length > SHOWN && (
        <Button
          variant="link"
          color="neutral"
          size="sm"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="-ms-2.5 h-10 self-start pointer-coarse:h-11"
        >
          {open ? "Hide changes" : "Show all changes"}
        </Button>
      )}
    </Stack>
  );
};
